import { API_URL } from 'config';
import React, { useState, useEffect } from 'react';

interface BannerType {
  _id: string;
  imageUrl: string;
  order: number;
}

interface BannerProps {
  banners: BannerType[];
  interval?: number;
}

const Banner: React.FC<BannerProps> = ({ banners, interval = 5000 }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imageRatios, setImageRatios] = useState<Record<string, number>>({});

  useEffect(() => {
    if (banners.length === 0) return;
    
    const timer = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % banners.length);
    }, interval);

    return () => clearInterval(timer);
  }, [banners.length, interval]);

  if (banners.length === 0) {
    return null;
  }

  const getFullImageUrl = (imageUrl: string) => {
    if (imageUrl.startsWith('http')) return imageUrl;
    return `${API_URL}${imageUrl}`;
  };

  const getBannerLinkByDate = () => {
    const now = new Date();
    const firstEventTime = new Date('2026-05-06T15:00:00+03:00');
    const secondEventTime = new Date('2026-05-07T09:00:00+03:00');

    if (now < firstEventTime) {
      return 'https://b24-rqdx87.bitrix24.site/crm_form_iym44/';
    }

    if (now < secondEventTime) {
      return 'https://b24-rqdx87.bitrix24.site/crm_form_nuemb/';
    }

    return null;
  };

  const currentBanner = banners[currentIndex];
  const currentAspectRatio = currentBanner
    ? imageRatios[currentBanner._id] ?? 16 / 9
    : 16 / 9;
  const activeBannerLink = getBannerLinkByDate();

  const handleImageLoad = (bannerId: string, event: React.SyntheticEvent<HTMLImageElement>) => {
    const { naturalWidth, naturalHeight } = event.currentTarget;
    if (!naturalWidth || !naturalHeight) {
      return;
    }

    const ratio = naturalWidth / naturalHeight;
    setImageRatios((prev) => {
      if (prev[bannerId] === ratio) {
        return prev;
      }
      return { ...prev, [bannerId]: ratio };
    });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
      <div
        className="relative w-full overflow-hidden rounded-3xl border border-white/70 shadow-[0_24px_45px_-32px_rgba(30,64,175,0.85)] transition-[aspect-ratio] duration-500"
        style={{ aspectRatio: currentAspectRatio }}
      >
        {activeBannerLink ? (
          <a
            href={activeBannerLink}
            target="_blank"
            rel="noopener noreferrer"
            className="absolute inset-0 block cursor-pointer"
            aria-label="Открыть регистрацию на мероприятие"
          >
            {banners.map((banner, index) => (
              <div
                key={banner._id}
                className={`absolute inset-0 w-full h-full transition-opacity duration-1000 ${
                  index === currentIndex ? 'opacity-100' : 'opacity-0'
                }`}
              >
                <img
                  src={getFullImageUrl(banner.imageUrl)}
                  alt={`Banner ${index + 1}`}
                  onLoad={(event) => handleImageLoad(banner._id, event)}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
            <div className="absolute inset-0 bg-gradient-to-r from-[#08215e]/40 via-transparent to-[#0f2f75]/30" />
          </a>
        ) : (
          <>
            {banners.map((banner, index) => (
              <div
                key={banner._id}
                className={`absolute inset-0 w-full h-full transition-opacity duration-1000 ${
                  index === currentIndex ? 'opacity-100' : 'opacity-0'
                }`}
              >
                <img
                  src={getFullImageUrl(banner.imageUrl)}
                  alt={`Banner ${index + 1}`}
                  onLoad={(event) => handleImageLoad(banner._id, event)}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
            <div className="absolute inset-0 bg-gradient-to-r from-[#08215e]/40 via-transparent to-[#0f2f75]/30" />
          </>
        )}
        <div className="absolute z-10 bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
          {banners.map((banner, index) => (
            <button
              key={banner._id}
              className={`w-2.5 h-2.5 rounded-full transition-all ${
                index === currentIndex ? 'bg-white scale-125' : 'bg-white/55 hover:bg-white/75'
              }`}
              onClick={() => setCurrentIndex(index)}
              aria-label={`Перейти к баннеру ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default Banner; 